import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getTodayDateString } from '../../../utils/dateFormatters';
import { PendingSaleSummary } from '../../../types/sale/dto/PendingSaleSummary';
import { IndividualPayments } from '../../../types/sale/payment/IndividualPayments';
import { SaleFormValues, SaleProduct, SaleSummary } from '../../../types/sale';
import { PaymentItemDTO } from '../../../types/sale/dto/PaymentItemDTO';
import { PendingCashierSaleCardDTO } from '../../../types/sale/dto/PendingCashierSaleCardDTO';
import {
    normalizeSaleProductAmounts,
    normalizeSaleProducts,
    normalizeSaleTotals,
} from '../../../utils/mappers/sale/normalizeSaleAmounts';

interface SaleState {
    form: SaleFormValues;
    activeBranchId: number | null;
    selectedProduct: SaleProduct | null;
    showMultiplePaymentModal: boolean;
    formErrors: Record<string, string>;
    saleSaved: boolean;
    saleLoaded: boolean;
    saleToViewLoaded: boolean;
    pendingSaleCount: number;
    finalizedSaleCount: number;
    pendingSaleList: PendingSaleSummary[];
    pendingCashierSales: PendingCashierSaleCardDTO[];
    finalizedSaleList: PendingSaleSummary[];
    filteredSaleList: SaleSummary[];
    paginator: any;
    pageSize: number;
    isSubmitting: boolean;
    activeDocumentType: SaleFormValues['documentType'] | null;
    paymentConditionCode: string | null;
    showSalePdfModal: boolean;
    showPendingSaleModal: boolean;
    showPendingCashierModal: boolean;
    showDtePdfModal: boolean;
    isCashierProcessingMode: boolean;
    pendingCashierRefreshCounter: number;
}

const initialFormState: SaleFormValues = {
    id: null,
    correlativeId: 0,
    documentType: null,
    documentNumber: null,
    saleDate: getTodayDateString(),
    customer: null,
    branch: null,
    paymentCondition: null,
    cancellationReason: null,
    comments: null,
    products: [],
    paymentMethod: undefined,
    totals: undefined,
    totalsView: undefined,

    individualPayments: {
        cash: undefined,               // 01
        debitCard: undefined,          // 02
        creditCard: undefined,         // 03
        check: undefined,              // 04
        bankTransfer: undefined,       // 05
        electronicMoney: undefined,    // 08
        eWallet: undefined,            // 09
        bitcoin: undefined,            // 11
        crypto: undefined,             // 12
        accountsReceivable: undefined, // 13
        bankDraft: undefined,          // 14
        other: undefined,              // 99
    },

    payments: [],

    creditNoteId: undefined,
    creditFiscalId: undefined,

    exportInvoiceExtra: {
        incoterm: null,
        exportRegime: null,
        fiscalPrecinct: null,
    },
};


const initialState: SaleState = {
    form: initialFormState,
    activeBranchId: null,
    selectedProduct: null,
    showMultiplePaymentModal: false,
    saleSaved: false,
    saleLoaded: false,
    saleToViewLoaded: false,
    pendingSaleCount: 0,
    finalizedSaleCount: 0,
    pendingSaleList: [],
    pendingCashierSales: [],
    finalizedSaleList: [],
    filteredSaleList: [],
    paginator: null,
    pageSize: 10,
    isSubmitting: false,
    formErrors: {},
    activeDocumentType: null,
    paymentConditionCode: null,
    showSalePdfModal: false,
    showPendingSaleModal: false,
    showPendingCashierModal: false,
    showDtePdfModal: false,
    isCashierProcessingMode: false,
    pendingCashierRefreshCounter: 0,
};

const saleSlice = createSlice({
    name: 'sale',
    initialState,
    reducers: {

        /**
         * 🧾 Establece el tipo de documento activo para la venta.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto `SimpleEntity` que representa el tipo de documento seleccionado.
         *
         * ✅ Este reducer se utiliza cuando:
         *  - Se selecciona un tipo de documento al iniciar o cambiar el formulario de venta.
         *  - Se necesita conocer el tipo activo para renderizar secciones condicionales (por ejemplo, consumidor final, crédito fiscal, etc.).
         */
        setActiveDocumentType(state, action: PayloadAction<SaleFormValues['documentType']>) {
            state.activeDocumentType = action.payload;
        },

        setActiveBranchId(
            state,
            action: PayloadAction<number | null>
        ) {
            state.activeBranchId = action.payload;
        },


        /**
         * 💾 Indica si la venta ha sido guardada correctamente.
         *
         * @param state - Estado actual del slice.
         * @param action - Valor booleano que indica si la venta fue guardada exitosamente.
         *
         * ✅ Este flag se puede usar para:
         *  - Mostrar una notificación o alerta después de guardar.
         *  - Ejecutar redireccionamientos o limpieza del formulario.
         */
        setSaleSaved(state, action: PayloadAction<boolean>) {
            state.saleSaved = action.payload;
        },

        /**
         * 🔄 Indica si los datos de la venta han sido cargados completamente.
         *
         * @param state - Estado actual del slice.
         * @param action - Valor booleano que representa si la venta está completamente cargada.
         *
         * ✅ Se utiliza cuando:
         *  - Se carga una venta desde el backend para edición o visualización.
         *  - Se controlan spinners o bloqueos de interfaz hasta que los datos estén disponibles.
         */
        setSaleLoaded(state, action: PayloadAction<boolean>) {
            state.saleLoaded = action.payload;
        },

        /**
         * 👁️ Indica si los datos de la venta a visualizar han sido cargados.
         *
         * @param state - Estado actual del slice.
         * @param action - Valor booleano que indica si los datos de visualización están listos.
         *
         * ✅ Se usa para:
         *  - Mostrar el modal o vista de detalle de la venta.
         *  - Evitar renderizar componentes de detalle sin datos completos.
         */
        setSaleToViewLoaded(state, action: PayloadAction<boolean>) {
            state.saleToViewLoaded = action.payload;
        },

        /**
         * ⏳ Marca si el formulario de venta está en proceso de envío.
         *
         * @param state - Estado actual del slice.
         * @param action - Valor booleano que indica si el formulario está siendo enviado.
         *
         * ✅ Útil para:
         *  - Mostrar spinners o desactivar botones mientras se envía la venta.
         *  - Evitar múltiples envíos simultáneos.
         */
        setIsSubmitting(state, action: PayloadAction<boolean>) {
            state.isSubmitting = action.payload;
        },

        /**
         * ❌ Limpia todos los errores de validación del formulario de venta.
         *
         * @param state - Estado actual del slice.
         *
         * ✅ Se utiliza para:
         *  - Reiniciar los mensajes de error antes de una nueva validación.
         *  - Asegurar que errores antiguos no persistan visualmente tras una corrección.
         */
        clearFormErrors(state) {
            state.formErrors = {};
        },


        /**
         * ⚠️ Asigna los errores de validación al formulario de venta.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto con claves del formulario y mensajes de error correspondientes.
         *
         * ✅ Este reducer se usa para:
         *  - Mostrar errores específicos en los campos del formulario.
         *  - Manejar respuestas de validación del backend o del frontend.
         */
        setFormErrors(state, action: PayloadAction<Record<string, string>>) {
            state.formErrors = action.payload;
        },


        /**
         * ✏️ Actualiza un campo individual dentro del formulario de venta.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto con la propiedad `field` (clave del formulario) y `value` (nuevo valor).
         *
         * ✅ Ideal para:
         *  - Cambios controlados por el usuario en inputs.
         *  - Reutilizar lógica en componentes de formulario dinámicos.
         */
        updateSaleField(state, action: PayloadAction<{ field: keyof SaleFormValues; value: any }>) {
            const { field, value } = action.payload;
            state.form = {
                ...state.form,
                [field]: value,
            };
        },


        /**
         * 💳 Actualiza uno o varios campos del objeto `individualPayments` del formulario de venta.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto parcial de `IndividualPayments` con los campos que se desean actualizar.
         *
         * ✅ Ideal para:
         *  - Actualizar datos de forma de pago individual (efectivo, tarjeta, transferencia, crédito).
         *  - Evitar sobrescribir accidentalmente el objeto completo de `individualPayments`.
         *  - Mantener la lógica de actualización clara y segura.
         *
         * Ejemplo de uso:
         * ```ts
         * dispatch(updateIndividualPayments({
         *   cash: {
         *     amountReceived: 10,
         *     change: 5
         *   }
         * }));
         * ```
         */
        updateIndividualPayments(state, action: PayloadAction<Partial<IndividualPayments>>) {
            state.form.individualPayments = {
                ...state.form.individualPayments,
                ...action.payload
            };
        },

        setPayments(
            state,
            action: PayloadAction<PaymentItemDTO[]>
        ) {
            state.form.payments = action.payload;
        },

        addPaymentItem(
            state,
            action: PayloadAction<PaymentItemDTO>
        ) {
            state.form.payments.push(action.payload);
        },


        updatePaymentItem(
            state,
            action: PayloadAction<{ index: number; item: PaymentItemDTO }>
        ) {
            const { index, item } = action.payload;

            if (state.form.payments[index]) {
                state.form.payments[index] = item;
            }
        },

        removePaymentItem(
            state,
            action: PayloadAction<number>
        ) {
            state.form.payments.splice(action.payload, 1);
        },

        clearPayments(state) {
            state.form.payments = [];
        },


        /**
         * 🧹 Limpia por completo el objeto `individualPayments` del formulario de venta.
         *
         * @param state - Estado actual del slice.
         *
         * ✅ Se usa típicamente cuando:
         *  - Se cambia el `paymentMethod` y se desea limpiar los datos previos.
         *  - Se reinicia el formulario de venta.
         *  - Se evita enviar datos no deseados al backend.
         */
        clearIndividualPayments(state) {
            state.form.individualPayments = {
                cash: undefined,               // 01
                debitCard: undefined,          // 02
                creditCard: undefined,         // 03
                check: undefined,              // 04
                bankTransfer: undefined,       // 05
                electronicMoney: undefined,    // 08
                eWallet: undefined,            // 09
                bitcoin: undefined,            // 11
                crypto: undefined,             // 12
                accountsReceivable: undefined, // 13
                bankDraft: undefined,          // 14
                other: undefined,              // 99
            };
        },





        /**
         * 🧼 Reinicia completamente el formulario de venta al estado inicial.
         *
         * @param state - Estado actual del slice.
         *
         * ✅ Este reducer se utiliza cuando:
         *  - Se desea limpiar el formulario tras guardar o cancelar una venta.
         *  - Se inicia el registro de una nueva venta desde cero.
         *
         * 🔄 Realiza:
         *  - Limpieza del formulario (`form`) y errores (`formErrors`).
         *  - Reinicio de los indicadores de carga y guardado.
         *  - Limpieza del tipo de documento activo y del estado de envío.
         */
        clearSaleForm(state) {
            state.form = initialFormState;
            state.activeBranchId = null;
            state.formErrors = {};
            state.saleSaved = false;
            state.saleLoaded = false;
            state.saleToViewLoaded = false;
            state.activeDocumentType = null;
            state.isSubmitting = false;
        },


        /**
         * 🔢 Establece el contador total de ventas pendientes.
         *
         * @param state - Estado actual del slice.
         * @param action - Número de ventas pendientes a registrar.
         *
         * ✅ Se aplica cuando:
         *  - Se consulta el número total de ventas en estado pendiente desde el backend.
         *  - Se actualiza el contador tras una operación de carga.
         */
        setPendingSaleCount(state, action: PayloadAction<number>) {
            state.pendingSaleCount = action.payload;
        },


        /**
         * ➖ Disminuye en 1 el contador de ventas pendientes, si es mayor que 0.
         *
         * @param state - Estado actual del slice.
         *
         * ⚠️ Este método:
         *  - Previene que el contador llegue a valores negativos.
         *  - Se usa típicamente después de completar o cancelar una venta pendiente.
         */
        decreasePendingSaleCount(state) {
            if (state.pendingSaleCount > 0) {
                state.pendingSaleCount -= 1;
            }
        },


        /**
         * ✅ Establece el número total de ventas finalizadas.
         *
         * @param state - Estado actual del slice.
         * @param action - Número de ventas en estado finalizado.
         *
         * 📌 Se utiliza generalmente al cargar estadísticas desde el backend.
         */
        setFinalizedSaleCount(state, action: PayloadAction<number>) {
            state.finalizedSaleCount = action.payload;
        },


        /**
         * ➖ Disminuye en 1 el contador de ventas finalizadas, si es mayor que cero.
         *
         * @param state - Estado actual del slice.
         *
         * 🧠 Previene valores negativos en el contador.
         * 🛠️ Útil tras una anulación o reversión de venta finalizada.
         */
        decreaseFinalizedSaleCount(state) {
            if (state.finalizedSaleCount > 0) {
                state.finalizedSaleCount -= 1;
            }
        },


        /**
         * 📥 Asigna la lista completa de ventas pendientes.
         *
         * @param state - Estado actual del slice.
         * @param action - Arreglo de objetos `SaleSummary` correspondientes a ventas en estado pendiente.
         *
         * ✅ Se aplica después de obtener datos paginados desde el backend.
         */
        setPendingSaleList(state, action: PayloadAction<PendingSaleSummary[]>) {
            state.pendingSaleList = action.payload;
        },

        /**
         * 💵 Establece la lista
         * de ventas pendientes
         * de cobro para caja.
         */
        setPendingCashierSales(
            state,
            action: PayloadAction<PendingCashierSaleCardDTO[]>
        ) {
            state.pendingCashierSales = action.payload;
        },

        /**
         * 🕓 Abre el modal
         * de ventas pendientes
         * de cobro.
         */
        setOpenPendingCashierModal(state) {
            state.showPendingCashierModal = true;
        },

        /**
         * ❌ Cierra el modal
         * de ventas pendientes
         * de cobro.
         */
        setClosePendingCashierModal(state) {
            state.showPendingCashierModal = false;
        },

        /**
         * 💵 Activa/desactiva
         * modo procesamiento
         * de caja.
         */
        setCashierProcessingMode(
            state,
            action: PayloadAction<boolean>
        ) {
            state.isCashierProcessingMode =
                action.payload;
        },

        /**
         * 🗑️ Elimina una venta
         * pendiente de cobro
         * por ID.
         */
        removePendingCashierSaleById(
            state,
            action: PayloadAction<number>
        ) {
            state.pendingCashierSales =
                state.pendingCashierSales.filter(
                    sale => sale.saleId !== action.payload
                );
        },


        triggerPendingCashierRefresh(state) {
            state.pendingCashierRefreshCounter++;
        },


        /**
         * 📦 Asigna la lista completa de ventas finalizadas.
         *
         * @param state - Estado actual del slice.
         * @param action - Arreglo de objetos `SaleSummary` correspondientes a ventas en estado finalizado.
         *
         * 🧩 Se utiliza tras una consulta de historial o filtrado de ventas completas.
         */
        setFinalizedSaleList(state, action: PayloadAction<PendingSaleSummary[]>) {
            state.finalizedSaleList = action.payload;
        },


        /**
         * 🗑️ Elimina una venta pendiente de la lista por su ID.
         *
         * @param state - Estado actual del slice.
         * @param action - ID de la venta que debe eliminarse.
         *
         * 🧠 Útil cuando una venta pendiente se finaliza o cancela.
         */
        removePendingSaleById(state, action: PayloadAction<number>) {
            state.pendingSaleList = state.pendingSaleList.filter(s => s.id !== action.payload);
        },


        /**
         * 🗑️ Elimina una venta finalizada de la lista por su ID.
         *
         * @param state - Estado actual del slice.
         * @param action - ID de la venta que debe eliminarse.
         *
         * 🧠 Se puede utilizar cuando se anula una venta finalizada.
         */
        removeFinalizedSaleById(state, action: PayloadAction<number>) {
            state.finalizedSaleList = state.finalizedSaleList.filter(s => s.id !== action.payload);
        },


        /**
         * 🔍 Establece la lista filtrada de ventas junto con su paginador.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto que contiene:
         *   - `content`: arreglo con las ventas resultantes del filtro.
         *   - información del paginador.
         *
         * ✅ Se aplica al realizar una búsqueda avanzada o consulta con filtros.
         */
        setFilteredSaleList(state, action: PayloadAction<{ content: SaleSummary[] }>) {
            state.filteredSaleList = action.payload.content;
            state.paginator = action.payload;
        },


        /**
         * ♻️ Limpia la lista de ventas filtradas y resetea el paginador.
         *
         * @param state - Estado actual del slice.
         *
         * 🔄 Se utiliza, por ejemplo, al cambiar de vista o al reiniciar filtros.
         */
        clearFilteredSaleList(state) {
            state.filteredSaleList = [];
            state.paginator = null;
        },


        /**
         * 📄 Establece el número de resultados por página para la paginación.
         *
         * @param state - Estado actual del slice.
         * @param action - Número entero que representa el tamaño de página.
         *
         * ✅ Se utiliza cuando el usuario cambia el tamaño de página en una tabla.
         */
        setPageSize(state, action: PayloadAction<number>) {
            state.pageSize = action.payload;
        },


        /**
         * 🔄 Actualiza el estado (`status`) de una venta específica dentro de la lista filtrada.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto que contiene:
         *   - `id`: ID de la venta a actualizar.
         *   - `status`: Nuevo estado de la venta (ej. FINALIZED, PENDING, CANCELLED).
         *
         * 🧠 Útil para cambiar el estado visual de una venta en tiempo real sin recargar toda la lista.
         */
        updateSaleStatus(
            state,
            action: PayloadAction<{ id: number; status: SaleSummary['status']; hasDteError?: boolean }>
        ) {
            const { id, status, hasDteError } = action.payload;
            const index = state.filteredSaleList.findIndex((s) => s.id === id);
            if (index !== -1) {
                state.filteredSaleList[index].status = status;
                if (hasDteError !== undefined) state.filteredSaleList[index].hasDteError = hasDteError;
            }
        },

        updateSaleDteStatus(
            state,
            action: PayloadAction<{ id: number; dteStatus: string; hasDteError?: boolean }>
        ) {
            const { id, dteStatus, hasDteError } = action.payload;
            const sale = state.filteredSaleList.find((item) => item.id === id);
            if (sale) {
                sale.dteStatus = dteStatus;
                if (hasDteError !== undefined) sale.hasDteError = hasDteError;
            }
        },

        /**
         * 🔄 Actualiza número de documento y estado de una venta en la lista filtrada.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto con:
         *   - id: ID de la venta
         *   - status: nuevo estado
         *   - documentNumber: nuevo número de documento
         */
        updateSaleAfterForceAnnul(
            state,
            action: PayloadAction<{
                id: number;
                status: SaleSummary['status'];
                documentNumber: string;
            }>
        ) {
            const { id, status, documentNumber } = action.payload;

            const index = state.filteredSaleList.findIndex((s) => s.id === id);

            if (index !== -1) {
                state.filteredSaleList[index].status = status;
                state.filteredSaleList[index].documentNumber = documentNumber;
            }
        },


        /**
         * 🧠 Asigna el formulario de venta con los datos proporcionados.
         */
        setSaleForm(state, action: PayloadAction<SaleFormValues>) {
            state.form = {
                ...initialFormState,
                ...action.payload,
                products: normalizeSaleProducts(action.payload.products),
                totals: normalizeSaleTotals(action.payload.totals)
            };
            state.formErrors = {};
        },


        /**
         * 💰 Actualiza los totales de la venta.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto `SaleTotals` con los nuevos valores de totales.
         *
         * ✅ Este reducer se utiliza cuando:
         *  - Se recalculan los totales tras agregar, editar o eliminar un producto.
         *  - Se reciben nuevos totales desde el backend.
         */
        updateSaleTotals(state, action: PayloadAction<SaleFormValues['totals']>) {
            state.form.totals = normalizeSaleTotals(action.payload);
        },

        /**
         * 📊 Actualiza la vista dinámica de totales (controlada por backend).
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto SaleTotalsView con los ítems que deben mostrarse.
         *
         * ✅ Se utiliza cuando:
         *  - El backend envía qué campos deben mostrarse según tipo de documento.
         *  - Factura de exportación (flete, seguro, etc.)
         *  - Futuras reglas dinámicas por documento.
         */
        updateSaleTotalsView(state, action: PayloadAction<SaleFormValues['totalsView']>) {
            state.form.totalsView = action.payload;
        },


        /**
         * ➕ Agrega o reemplaza un producto en el formulario de venta.
         */
        addSaleProduct(state, action: PayloadAction<SaleProduct>) {
            state.form.products.push(normalizeSaleProductAmounts(action.payload));
        },

        /**
         * 🔄 Actualiza un producto en la lista de productos de la venta.
         */
        updateSaleProduct(state, action: PayloadAction<SaleProduct>) {
            const updated = action.payload;

            const index = state.form.products.findIndex(
                p => p.saleDetailId === updated.saleDetailId
            );

            if (index !== -1) {
                state.form.products[index] = normalizeSaleProductAmounts(updated);
            }
        },

        /**
         * 🗑️ Elimina un producto del formulario de venta por su ID.
         */
        deleteSaleProduct(state, action: PayloadAction<number>) {
            state.form.products = state.form.products.filter(
                product => product.saleDetailId !== action.payload
            );
        },

        /**
         * 📥 Establece una lista completa de productos en el formulario de venta.
         *
         * @param state - Estado actual del slice.
         * @param action - Arreglo de objetos `SaleProduct` que representan los productos de la venta.
         *
         * ✅ Ideal para:
         *  - Operaciones masivas como devolución total desde nota de crédito.
         *  - Cargar venta existente desde backend con sus productos.
         */
        setSaleProducts(state, action: PayloadAction<SaleProduct[]>) {
            state.form.products = normalizeSaleProducts(action.payload);
        },


        /**
         * 🎯 Establece el producto seleccionado para edición o vista.
         */
        setSelectedProduct(state, action: PayloadAction<SaleProduct | null>) {
            state.selectedProduct = action.payload;
        },

        /**
         * 🔄 Actualiza los datos del producto seleccionado en el estado (si aplica).
         */
        updateSelectedProductField(state, action: PayloadAction<Partial<SaleProduct>>) {
            if (state.selectedProduct) {
                state.selectedProduct = {
                    ...state.selectedProduct,
                    ...action.payload,
                };
            }
        },

        /**
         * 📂 Acción para mostrar el modal de pagos múltiples.
         */
        setShowMultiplePaymentModal(state) {
            state.showMultiplePaymentModal = true;
        },

        /**
         * ❌ Acción para ocultar el modal de pagos múltiples.
         */
        setHideMultiplePaymentModal(state) {
            state.showMultiplePaymentModal = false;
        },

        resetSaleFormCompletely(state) {
            state.form = initialFormState;
            state.activeBranchId = null;
            state.formErrors = {};
            state.saleSaved = false;
            state.saleLoaded = false;
            state.saleToViewLoaded = false;
            state.activeDocumentType = null;
            state.selectedProduct = null;
            state.isSubmitting = false;
            // state.pendingSaleCount = 0;
            // state.finalizedSaleCount = 0;
            // state.pendingSaleList = [];
            // state.finalizedSaleList = [];
            // state.filteredSaleList = [];
            state.paginator = null;
        },

        /**
         * 🔁 Actualiza los IDs de referencia de una nota de crédito asociada.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto con posibles valores de `creditNoteId` y/o `creditFiscalId`.
         *
         * ✅ Se utiliza cuando:
         *  - Se carga una venta que proviene de una nota de crédito.
         *  - Se desea rastrear la relación entre la venta actual y su nota de crédito origen.
         */
        updateCreditReferences(
            state,
            action: PayloadAction<{
                creditNoteId?: number;
                creditFiscalId?: number;
            }>
        ) {
            state.form.creditNoteId = action.payload.creditNoteId ?? state.form.creditNoteId;
            state.form.creditFiscalId = action.payload.creditFiscalId ?? state.form.creditFiscalId;
        },

        /**
         * 🌎 Actualiza la sección de metadatos de factura de exportación.
         *
         * @param state - Estado actual del slice.
         * @param action - Metadata completo recibido desde backend.
         *
         * ✅ Se usa cuando:
         *  - Se actualiza incoterm
         *  - Se actualiza régimen
         *  - Se actualiza recinto fiscal
         *  - Backend limpia metadata automáticamente
         */
        setExportInvoiceMetadata(
            state,
            action: PayloadAction<SaleFormValues['exportInvoiceExtra']>
        ) {
            state.form.exportInvoiceExtra = action.payload;
        },


        /**
         * ☑️ Establece solo el ID del Crédito Fiscal asociado.
         *
         * @param state - Estado actual del slice.
         * @param action - ID del crédito fiscal (`creditFiscalId`) a establecer.
         *
         * ✅ Se utiliza cuando:
         *  - Se selecciona una venta con un crédito fiscal desde el buscador.
         *  - Se necesita guardar el `creditFiscalId` para poder usarlo luego en acciones como "Devolver Todos",
         *    donde se requiere el ID del crédito fiscal original para replicar los productos en la nota de crédito.
         */
        setCreditFiscalId(state, action: PayloadAction<number>) {
            state.form.creditFiscalId = action.payload;
        },


        /**
         * 🧹 Limpia las referencias de nota de crédito asociadas a la venta.
         *
         * @param state - Estado actual del slice.
         *
         * ✅ Se usa típicamente cuando:
         *  - Se reinicia el formulario de venta.
         *  - Se elimina la relación con una nota de crédito previamente asociada.
         */
        clearCreditReferences(state) {
            state.form.creditNoteId = undefined;
            state.form.creditFiscalId = undefined;
        },

        /**
         * 🧹 Limpia todos los productos del formulario de venta.
         *
         * ✅ Útil cuando:
         *  - Se desea reiniciar la lista de productos sin limpiar todo el formulario.
         *  - Se borra una referencia de nota de crédito y se deben quitar los productos acreditados.
         */
        clearSaleProducts(state) {
            state.form.products = [];
        },

        /**
         * 🔄 Actualiza el código de la condición de pago seleccionada
         *
         * @param state - Estado actual del slice.
         * @param action - String con el código (por ejemplo: 'CONTADO', 'CREDITO', 'OTRO').
         *
         * ✅ Este reducer te permite saber en cualquier parte del estado cuál fue
         * la condición seleccionada para mostrar/ocultar secciones dinámicamente.
         */
        setPaymentConditionCode(state, action: PayloadAction<string | null>) {
            state.paymentConditionCode = action.payload;
        },


        /**
         * 📄 Abre el modal de visualización de venta en formato PDF o ticket.
         *
         * ✅ Este reducer se utiliza cuando:
         *  - El usuario desea **visualizar una venta** en formato PDF (tamaño carta) o en formato ticket.
         *  - Se abre un modal que permite al usuario seleccionar el tipo de visualización deseado.
         *  - Posteriormente, se renderiza el documento correspondiente dentro del modal.
         *
         * 📌 Nota: Este modal no edita datos, solo muestra una vista previa en PDF o ticket.
         */
        setOpenSalePdfModal(state) {
            state.showSalePdfModal = true;
        },

        /**
         * ❌ Cierra el modal de visualización de la venta en formato PDF o ticket.
         *
         * ✅ Este reducer se utiliza cuando:
         *  - El usuario cierra manualmente el modal de vista previa.
         *  - Se desea restablecer el estado de visualización después de mostrar el documento.
         */
        setCloseSalePdfModal(state) {
            state.showSalePdfModal = false;
        },


        /**
         * 🕓 Abre el modal de ventas pendientes.
         *
         * ✅ Este reducer se utiliza cuando:
         *  - El usuario hace clic en "Ventas pendientes" desde la vista de historial.
         *  - Se desea mostrar una lista de ventas en estado pendiente que pueden retomarse o cancelarse.
         *
         * 📌 El modal muestra un resumen de las ventas pendientes y permite acciones como continuar o anular.
         */
        setOpenPendingSaleModal(state) {
            state.showPendingSaleModal = true;
        },

        /**
         * ❌ Cierra el modal de ventas pendientes.
         *
         * ✅ Este reducer se utiliza cuando:
         *  - El usuario cierra el modal manualmente.
         *  - Se completa o cancela una venta pendiente y ya no es necesario mostrar el resumen.
         *
         * 📌 Resetea únicamente la visibilidad del modal; no afecta los datos cargados.
         */
        setClosePendingSaleModal(state) {
            state.showPendingSaleModal = false;
        },


        /**
         * 🧠 Establece los metadatos clave de la venta actual (ID y sucursal).
         *
         * ✅ Se utiliza para centralizar información mínima necesaria para acciones globales
         *    como: consultar DTE, generar PDF o ticket, etc.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto con `id` y `branchId` de la venta.
         */
        setSaleMeta(state, action: PayloadAction<{ id: number; branchId: number }>) {
            state.form.id = action.payload.id;
            state.form.branch = {
                id: action.payload.branchId,
                name: '', // ← opcional, si no necesitas el nombre completo
            };
        },

        /**
         * 🧹 Limpia el ID y la sucursal actual de la venta.
         */
        clearSaleMeta(state) {
            state.form.id = null;
            state.form.branch = null;
        },

        /**
         * 📄 Abre el modal exclusivo de vista previa de DTE en PDF.
         *
         * ✅ Se utiliza cuando:
         *  - El usuario desea ver un DTE previamente emitido en formato PDF.
         *  - Este modal no permite cambiar el tipo ni ver ticket, solo visualiza el DTE en pantalla.
         */
        setOpenDtePdfModal(state) {
            state.showDtePdfModal = true;
        },

        /**
         * ❌ Cierra el modal exclusivo de DTE PDF.
         *
         * ✅ Se invoca al cerrar el modal manualmente o al finalizar la visualización.
         */
        setCloseDtePdfModal(state) {
            state.showDtePdfModal = false;
        },

        removeSaleFromList: (state, action: PayloadAction<number>) => {
            state.filteredSaleList = state.filteredSaleList.filter(sale => sale.id !== action.payload);
        },


        /**
         * 🚫 Elimina una venta cancelada de la lista del historial.
         *
         * Se utiliza cuando una venta pendiente es cancelada por el usuario
         * y deja de formar parte de las ventas disponibles.
         */
        removeCanceledPendingSale(
            state,
            action: PayloadAction<number>
        ) {
            state.pendingSaleList = state.pendingSaleList.filter(
                sale => sale.id !== action.payload
            );
        },


    },
});

export const {
    setSaleSaved,
    setSaleLoaded,
    setSaleToViewLoaded,
    setIsSubmitting,
    clearFormErrors,
    setFormErrors,
    updateSaleField,
    updateIndividualPayments,
    clearIndividualPayments,

    setPayments,
    addPaymentItem,
    updatePaymentItem,
    removePaymentItem,
    clearPayments,


    clearSaleForm,
    setActiveDocumentType,
    setActiveBranchId,
    setPendingSaleCount,
    decreasePendingSaleCount,
    setFinalizedSaleCount,
    decreaseFinalizedSaleCount,
    setPendingSaleList,
    setFinalizedSaleList,
    removePendingSaleById,
    removeFinalizedSaleById,
    setFilteredSaleList,
    clearFilteredSaleList,
    setPageSize,
    updateSaleStatus,
    updateSaleDteStatus,
    updateSaleAfterForceAnnul,
    setSaleForm,
    setSaleMeta,
    clearSaleMeta,
    updateSaleTotals,
    updateSaleTotalsView,
    updateSaleProduct,
    addSaleProduct,
    deleteSaleProduct,
    setSaleProducts,
    setSelectedProduct,
    updateSelectedProductField,
    setShowMultiplePaymentModal,
    setHideMultiplePaymentModal,
    resetSaleFormCompletely,
    updateCreditReferences,
    setExportInvoiceMetadata,
    setCreditFiscalId,
    clearCreditReferences,
    clearSaleProducts,
    setPaymentConditionCode,
    setOpenSalePdfModal,
    setCloseSalePdfModal,
    setOpenPendingSaleModal,
    setClosePendingSaleModal,
    setOpenDtePdfModal,
    setCloseDtePdfModal,
    removeSaleFromList,
    setPendingCashierSales,
    setOpenPendingCashierModal,
    setClosePendingCashierModal,
    setCashierProcessingMode,
    removePendingCashierSaleById,
    triggerPendingCashierRefresh,
    removeCanceledPendingSale
} = saleSlice.actions;

export default saleSlice.reducer;
